import type { ButtonHTMLAttributes, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Link, type LinkProps } from '@tanstack/react-router';
import { Icon } from '@/components/ui/Icon';
import { cn } from '@/lib/cn';

/**
 * Design system §04. One primary button per view; labels start with a verb.
 * Heights: sm 36 / md 44 / lg 52. Radius: control (10px), 12px at lg.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: LucideIcon;
  /** Shows a spinner and blocks input. Keep the label as the pending sentence. */
  loading?: boolean;
  fullWidth?: boolean;
  children?: ReactNode;
}

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'border-transparent bg-primary text-white hover:bg-primary-hover',
  secondary:
    'border-[1.5px] border-border-strong bg-surface text-ink hover:border-primary hover:bg-primary-tint',
  ghost:
    'border-transparent bg-transparent text-primary hover:bg-primary-tint hover:text-primary-hover',
  danger: 'border-transparent bg-danger text-white hover:bg-danger-strong',
};

const DISABLED: Record<ButtonVariant, string> = {
  primary: 'disabled:border-transparent disabled:bg-border disabled:text-locked',
  secondary: 'disabled:border-border disabled:bg-paper disabled:text-locked',
  ghost: 'disabled:bg-transparent disabled:text-locked',
  danger: 'disabled:border-transparent disabled:bg-border disabled:text-locked',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-9 gap-1.5 rounded-chip px-3.5 text-small',
  md: 'h-11 gap-2 rounded-control px-5 text-body',
  lg: 'h-13 gap-2 rounded-[12px] px-6.5 text-[17px]',
};

/**
 * Button styling for elements that must be a link, not a `<button>`
 * (router `<Link>`, `<a href>`). Same visual spec, no behaviour.
 */
export function buttonClassName(
  {
    variant = 'primary',
    size = 'md',
    fullWidth = false,
  }: Pick<ButtonProps, 'variant' | 'size' | 'fullWidth'> = {},
  className?: string,
): string {
  return cn(
    'focus-ring inline-flex shrink-0 items-center justify-center border font-bold no-underline transition-colors duration-150',
    SIZE[size],
    VARIANT[variant],
    fullWidth && 'w-full',
    className,
  );
}

function Spinner({ tone }: { tone: 'onFill' | 'onSurface' }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-block size-4 shrink-0 rounded-full border-2 [animation:ds-spin_0.8s_linear_infinite]',
        tone === 'onFill' ? 'border-white/35 border-t-white' : 'border-border border-t-primary',
      )}
    />
  );
}

export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  loading = false,
  fullWidth = false,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  const onFill = variant === 'primary' || variant === 'danger';

  return (
    <button
      type={type}
      disabled={disabled ?? loading}
      aria-busy={loading || undefined}
      className={cn(
        'focus-ring inline-flex shrink-0 items-center justify-center border font-bold transition-colors duration-150',
        'disabled:cursor-not-allowed',
        SIZE[size],
        VARIANT[variant],
        DISABLED[variant],
        fullWidth && 'w-full',
        variant === 'danger' && 'focus-ring-danger',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner tone={onFill ? 'onFill' : 'onSurface'} /> : null}
      {!loading && icon ? <Icon icon={icon} size={size === 'sm' ? 16 : 20} /> : null}
      {children}
    </button>
  );
}

/** Square icon-only button at touch-target size (44px) — flag, close, menu. */
export interface IconActionProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: LucideIcon;
  label: string;
  size?: ButtonSize;
  variant?: Extract<ButtonVariant, 'secondary' | 'ghost'>;
}

export function IconAction({
  icon,
  label,
  size = 'md',
  variant = 'secondary',
  className,
  type = 'button',
  ...rest
}: IconActionProps) {
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        'focus-ring grid shrink-0 place-items-center border transition-colors duration-150',
        'disabled:cursor-not-allowed disabled:text-locked',
        size === 'sm'
          ? 'size-9 rounded-chip'
          : size === 'lg'
            ? 'size-13 rounded-[12px]'
            : 'size-11 rounded-control',
        VARIANT[variant],
        DISABLED[variant],
        className,
      )}
      {...rest}
    >
      <Icon icon={icon} size={size === 'sm' ? 16 : 20} />
    </button>
  );
}

export interface TextLinkProps extends LinkProps {
  className?: string;
  children?: ReactNode;
  tone?: 'default' | 'muted';
}

export function TextLink({ className, children, tone = 'default', ...rest }: TextLinkProps) {
  return (
    <Link
      {...rest}
      className={cn(
        'focus-ring rounded-chip font-bold underline-offset-[3px] hover:underline',
        tone === 'muted' ? 'text-ink-2 hover:text-ink' : 'text-primary hover:text-primary-hover',
        className,
      )}
    >
      {children}
    </Link>
  );
}
